import type { ClintplayEvent, ConsoleEventData } from '../types';

type EventCallback = (event: ClintplayEvent) => void;
type ConsoleLevel = 'log' | 'warn' | 'error' | 'info' | 'debug';

const LEVELS: ConsoleLevel[] = ['log', 'warn', 'error', 'info', 'debug'];

export class ConsoleInterceptor {
  private originals: Partial<Record<ConsoleLevel, (...args: unknown[]) => void>> = {};

  constructor(
    private sessionId: string,
    private emit: EventCallback
  ) {}

  start(): void {
    for (const level of LEVELS) {
      this.originals[level] = console[level].bind(console);
      console[level] = (...args: unknown[]) => {
        const data: ConsoleEventData = {
          level,
          args: args.map((arg) => this.serialize(arg)),
        };
        this.emit({
          type: 'console',
          timestamp: Date.now(),
          sessionId: this.sessionId,
          data,
        });

        // Also emit console.error calls as error events so they count toward errorCount
        if (level === 'error') {
          const message = args.length > 0
            ? (args[0] instanceof Error ? args[0].message : String(args[0]))
            : 'console.error()';
          this.emit({
            type: 'error',
            timestamp: Date.now(),
            sessionId: this.sessionId,
            data: {
              message,
              type: 'onerror',
              source: 'console.error',
            },
          });
        }

        this.originals[level]?.(...args);
      };
    }
  }

  stop(): void {
    for (const level of LEVELS) {
      if (this.originals[level]) {
        console[level] = this.originals[level] as any;
      }
    }
    this.originals = {};
  }

  private serialize(arg: unknown): unknown {
    if (arg instanceof Error) {
      return { __type: 'Error', message: arg.message, stack: arg.stack };
    }
    if (typeof arg === 'object' && arg !== null) {
      try { JSON.stringify(arg); return arg; } catch { return String(arg); }
    }
    return arg;
  }
}
